import { useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import UserLink from '../userLink';
import OneOnOneRequestCard from '../oneOnOneRequestCard';
import OneOnOneTimeIndicator from '../oneOnOneTimeIndicator';
import RequestedIconTooltip from '../requestedIconTooltip';
import PlansIconTooltip from '../plansIconTooltip';
import TasksIconTooltip from '../tasksIconTooltip';
import RecurringIconTooltip from '../recuringIconTooltip';
import AsyncListBadge from '../asyncListBadge';
import OneOnOneCalendar from '../oneOnOneCalendar';
import Tag from '../tag';
import { ReactComponent as TagsIcon } from '../../../assets/icons/tag-dark.svg';
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock.svg';
import { formatDate, isDateInFuture } from '../../../utility/dateUtils';
import {
  prepareOneOnOneRequestCardLabels,
  isOneOneOneManagerChangedCheck,
} from '../../../utility/oneOnOne';
import { isArrayEmpty } from '../../../utility/helpers';
import {
  DATE_TIME_FORMAT,
  RECURRING_TYPES_MAP,
} from '../../../constants/oneOnOne';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    borderRadius: 16,
    cursor: 'default',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(4),
    width: 270,
    minHeight: 170,
    userSelect: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  accessible: {
    cursor: 'pointer',
  },
  request: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    borderRadius: 16,
    border: 'none',
    cursor: 'default',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(4),
    width: 270,
    minHeight: 170,
    userSelect: 'none',
    '&:hover': {
      border: 'none',
    },
  },
  requestContent: {
    paddingLeft: 0,
  },
  participantsWrapper: {
    marginTop: spacing(2),
  },
  userWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing(1.5),
  },
  user: {
    maxWidth: 'calc(100% - 30px)',
  },
  iconTooltip: {
    marginLeft: spacing(1.5),
  },
  meetingDateWrapper: {
    display: 'flex',
    alignItems: 'center',
    height: 24,
    marginTop: spacing(2.5),
  },
  meetingDate: {
    marginRight: spacing(1),
  },
  clockIcon: {
    marginTop: spacing(-0.5),
    marginRight: spacing(2),
  },
  infoWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(0,max-content))',
    gridColumnGap: 8,
    alignItems: 'center',
    marginTop: 'auto',
  },
  badge: {
    flexFlow: 'row-reverse',
    padding: spacing(0, 1.5),
    height: 22,
  },
  badgeIcon: {
    marginLeft: 0,
    marginRight: spacing(1),
    marginTop: spacing(-0.5),
  },
  tagCount: {
    fontSize: 14,
    lineHeight: '18px',
  },
  noSpacing: {
    marginLeft: 0,
  },
  calendarButton: {
    marginTop: spacing(-0.75),
  },
}));

const OneOnOneMiniCard = ({
  className = undefined,
  translations,
  oneOnOne = {},
  myManager = {},
  onCardClick = () => {},
  onEditRequest = () => {},
  onDeleteRequest = () => {},
}) => {
  const {
    moderator,
    user,
    meeting_time,
    has_impression,
    requested,
    isPendingRequest,
    tags,
    tasks,
    plans,
    recurring,
    canAccessModeratorProfile,
    canAccessUserProfile,
    isCreatedForMe,
    isCreatedByMe,
    canAccessOneOnOne,
  } = oneOnOne;
  const classes = useStyles({ isPendingRequest });
  const hasPlans = !isArrayEmpty(plans);
  const hasTasks = !isArrayEmpty(tasks);
  const hasTags = !isArrayEmpty(tags);
  const isRecurring = recurring !== RECURRING_TYPES_MAP.DOES_NOT_REPEAT.id;
  const isFuture = isDateInFuture(meeting_time);

  const getVirtualTagItemSize = useCallback(
    (index, items) => (index === items.length - 1 ? 20 : 28),
    []
  );

  const handleCardClick = useCallback(() => {
    if (!oneOnOne.isPendingRequest && oneOnOne.canAccessOneOnOne) {
      onCardClick(oneOnOne.id);
    }
  }, [oneOnOne, onCardClick]);

  const renderTag = tag => (
    <Tag tag={tag} color={tag?.category?.color} isSelected />
  );

  return isPendingRequest ? (
    <OneOnOneRequestCard
      className={classNames(classes.request, className)}
      translations={prepareOneOnOneRequestCardLabels(
        translations.requestCard,
        oneOnOne.moderator,
        myManager
      )}
      request={oneOnOne}
      isExpired={isOneOneOneManagerChangedCheck(oneOnOne.moderator, myManager)}
      onChangeRequest={onEditRequest}
      onDeleteRequest={onDeleteRequest}
      isUserCard
    />
  ) : (
    <div
      className={classNames(
        classes.root,
        { [classes.accessible]: canAccessOneOnOne },
        className
      )}
      onClick={handleCardClick}
    >
      <div className={classes.header}>
        <OneOnOneTimeIndicator
          labels={translations.statuses}
          meetingTime={meeting_time}
          hasImpression={has_impression}
        />
        <RecurringIconTooltip
          iconClass={classNames({ [classes.accessible]: canAccessOneOnOne })}
          isVisible={isRecurring}
          text={translations.recurring}
        />
      </div>
      <div className={classes.participantsWrapper}>
        <UserLink
          user={moderator}
          avatarProps={{
            variant: 'subtitle1',
          }}
          isUserAccessible={canAccessModeratorProfile}
        />
        <div className={classes.userWrapper}>
          <UserLink
            className={classNames({ [classes.user]: requested })}
            user={user}
            avatarProps={{
              variant: 'subtitle1',
            }}
            isUserAccessible={canAccessUserProfile}
          />
          {requested && (
            <RequestedIconTooltip
              className={classNames(classes.iconTooltip, {
                [classes.accessible]: canAccessOneOnOne,
              })}
              labels={translations.requested}
              isRequestedByCurrentUser={isCreatedForMe}
            />
          )}
        </div>
      </div>
      <div className={classes.meetingDateWrapper}>
        <ClockIcon className={classes.clockIcon} />
        <Typography
          className={classes.meetingDate}
          variant="body2"
        >{`${formatDate(meeting_time, DATE_TIME_FORMAT)}`}</Typography>
        {isFuture && (isCreatedByMe || isCreatedForMe) && (
          <OneOnOneCalendar
            className={classes.calendarButton}
            translations={translations.calendar}
            record={oneOnOne}
            isSmall
          />
        )}
      </div>
      <div className={classes.infoWrapper}>
        <PlansIconTooltip
          iconClass={classNames({ [classes.accessible]: canAccessOneOnOne })}
          text={translations.plansIconTooltip}
          isVisible={hasPlans}
          numberOfPlans={plans.length}
        />
        <TasksIconTooltip
          iconClass={classNames({ [classes.accessible]: canAccessOneOnOne })}
          text={translations.tasksIconTooltip}
          numberOfTasks={tasks.length}
          isVisible={hasTasks}
        />
        {hasTags && (
          <AsyncListBadge
            badgeClass={classes.badge}
            badgeIconClass={classes.badgeIcon}
            countClass={classes.tagCount}
            tooltipTitle={translations.tags}
            count={tags?.length}
            items={tags}
            icon={TagsIcon}
            getItemSize={getVirtualTagItemSize}
            shouldDisablePortal={false}
            renderItem={renderTag}
          />
        )}
      </div>
    </div>
  );
};

OneOnOneMiniCard.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  oneOnOne: PropTypes.object,
  myManager: PropTypes.object,
  onCardClick: PropTypes.func,
  onEditRequest: PropTypes.func,
  onDeleteRequest: PropTypes.func,
};

export default OneOnOneMiniCard;
